<template>
    <b-card v-if="invoice" title="Commodity details">
        <b-row
            v-for="(invoiceLine, index) in invoice.invoiceLines"
            v-bind:key="index"
            align-v="center"
            align-h="center"
        >
          <b-col>
            <b-form-group :label="$t('createInvoice.label.ItemNumber')">
              <b-input-group>
                <b-form-input
                    v-model="invoiceLine.itemNumber"
                    :name="`${index}-itemNumber`"
                    size="sm"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
            <b-col>
                <b-form-group :label="$t('createInvoice.label.HarmonizedCode')">
                  <b-input-group>
                    <b-form-input
                        v-model="invoiceLine.harmonizedCode"
                        :name="`${index}-harmonizedCode`"
                        size="sm"
                    ></b-form-input>
                
              <b-input-group-append v-if="invoiceLine.requireHsCode">
                <font-awesome-icon icon="exclamation-circle" size="lg" />
              </b-input-group-append>
              </b-input-group>
              <validationField
                  :fieldName="'Harmonized Code'"
                  v-if="v.invoice.invoiceLines.$each[index].harmonizedCode.$dirty"
                  :validator="v.invoice.invoiceLines.$each[index].harmonizedCode"
              ></validationField>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group :label="$t('createInvoice.label.Commodity')">
                    <b-input-group>
                        <b-form-input
                            v-model="invoiceLine.commodity"
                            :name="`${index}-commodity`"
                            size="sm"
                            :class="{
                                'is-invalid': v.invoice.invoiceLines.$each[index].commodity.$error
                            }"
                            @update="v.invoice.invoiceLines.$each[index].commodity.$touch()"
                        ></b-form-input>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                    </b-input-group>
                    <validationField
                        :fieldName="'Commodity'"
                        v-if="v.invoice.invoiceLines.$each[index].commodity.$dirty"
                        :validator="v.invoice.invoiceLines.$each[index].commodity"
                    ></validationField>
                </b-form-group>
            </b-col>
          <b-col>
            <b-form-group :label="$t('createInvoice.label.Manufacturer')">
              <b-input-group>
                <b-form-input
                    v-model="invoiceLine.manufacturer"
                    :name="`${index}-manufacturer`"
                    size="sm"
                    :class="{
                                'is-invalid': v.invoice.invoiceLines.$each[index].manufacturer.$error
                            }"
                    @update="v.invoice.invoiceLines.$each[index].manufacturer.$touch()"
                ></b-form-input>
              </b-input-group>
              <validationField
                  :fieldName="'Manufacturer'"
                  v-if="v.invoice.invoiceLines.$each[index].manufacturer.$dirty"
                  :validator="v.invoice.invoiceLines.$each[index].manufacturer"
              ></validationField>
            </b-form-group>
          </b-col>
            <b-col>
                <b-form-group :label="$t('createInvoice.label.CountryOfOrigin')">
                    <b-input-group>
                        <vSelect
                            :dynamicWidth="true"
                            style="width: 90%"
                            v-model="invoiceLine.countryOfOrigin"
                            :placeholder="$t('postalCode.modal.selectCountry')"
                            :options="getCountries"
                            label="countryName"
                            :name="`${index}-countryOfOrigin`"
                            :reduce="(country) => country.countryCode"
                            :class="{
                                'is-invalid':
                                    v.invoice.invoiceLines.$each[index].countryOfOrigin.$error
                            }"
                            @update="v.invoice.invoiceLines.$each[index].countryOfOrigin.$touch()"
                        />

                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                    </b-input-group>
                    <validationField
                        :fieldName="'Country of origin'"
                        v-if="v.invoice.invoiceLines.$each[index].countryOfOrigin.$dirty"
                        :validator="v.invoice.invoiceLines.$each[index].countryOfOrigin"
                    ></validationField>
                </b-form-group>
            </b-col>

            <b-col sm="1">
                <b-form-group :label="$t('createInvoice.label.Quantity')">
                    <b-input-group>
                        <b-form-input
                            v-model="invoiceLine.quantity"
                            class="form-control form-control-sm"
                            :name="`${index}-quantity`"
                            :class="{
                                'is-invalid': v.invoice.invoiceLines.$each[index].quantity.$error
                            }"
                            @update="v.invoice.invoiceLines.$each[index].quantity.$touch()"
                            type="number"
                        ></b-form-input>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                    </b-input-group>
                    <validationField
                        :fieldName="'Quantity'"
                        v-if="v.invoice.invoiceLines.$each[index].quantity.$dirty"
                        :validator="v.invoice.invoiceLines.$each[index].quantity"
                    ></validationField>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group :label="$t('createInvoice.label.UnitType')">
                    <b-input-group>
                        <b-form-select
                            size="sm"
                            v-model="invoiceLine.unitType"
                            :options="unitTypes"
                            class="form-control form-control-sm"
                            :name="`${index}-unitType`"
                            :class="{
                                'is-invalid': v.invoice.invoiceLines.$each[index].unitType.$error
                            }"
                            @update="v.invoice.invoiceLines.$each[index].unitType.$touch()"
                        ></b-form-select>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                    </b-input-group>
                    <validationField
                        :fieldName="'Unit type'"
                        v-if="v.invoice.invoiceLines.$each[index].unitType.$dirty"
                        :validator="v.invoice.invoiceLines.$each[index].unitType"
                    ></validationField>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group :label="$t('createInvoice.label.UnitPrice')">
                    <b-input-group>
                        <b-form-input
                            v-model="invoiceLine.unitPrice"
                            class="form-control form-control-sm"
                            :name="`${index}-unitPrice`"
                            :class="{
                                'is-invalid': v.invoice.invoiceLines.$each[index].unitPrice.$error
                            }"
                            type="number"
                            @update="v.invoice.invoiceLines.$each[index].unitPrice.$touch()"
                        ></b-form-input>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                        <validationField
                            :fieldName="'Unit type'"
                            v-if="v.invoice.invoiceLines.$each[index].unitPrice.$dirty"
                            :validator="v.invoice.invoiceLines.$each[index].unitPrice"
                        ></validationField>
                    </b-input-group>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group :label="$t('createInvoice.label.Total')">
                    <b-input-group>
                        <b-form-input
                            type="number"
                            :value="calculateLineTotal(invoiceLine)"
                            disabled
                            :name="`${index}-total`"
                            class="form-control form-control-sm"
                        ></b-form-input>
                    </b-input-group>
                </b-form-group>
            </b-col>
            <b-col v-if="invoice.invoiceLines.length > 1" style="max-width: fit-content">
              <b-form-group>
                <b-input-group>
                  <font-awesome-icon style="margin:1.5rem 0 0 -1rem; color: red; cursor:pointer;"
                                     icon="trash-alt" 
                                     size="lg"
                                     :name="`${index}-delete`"
                                     @click="removeInvoiceLine(invoiceLine)" />
                </b-input-group>
              </b-form-group>
            </b-col>
        </b-row>
        <b-row class="mt-4">
            <b-col cols="9">
                <b-row align-h="start" cols="2">
                    <b-col cols="1">
                        <b-button @click="addInvoiceLine" name="addInvoiceLine">{{
                            $t('createInvoice.button.Add')
                        }}</b-button>
                    </b-col>
                </b-row>
            </b-col>
            <b-col>
                <b-row class="pr-3 pl-3">
                    <b-col>
                        <b-row>
                            <b-col>
                                <span class="total-text">{{
                                    $t('createInvoice.label.SubTotal')
                                }}</span>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <span class="total-text">{{
                                    $t('createInvoice.label.FreightCost')
                                }}</span>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <span class="total-text">{{
                                    $t('createInvoice.label.InsuranceCost')
                                }}</span>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <span class="total-text">{{
                                    $t('createInvoice.label.OtherCost')
                                }}</span>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <span class="total-text">{{
                                    $t('createInvoice.label.Total')
                                }}</span>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col>
                        <b-row>
                            <b-form-group>
                                <b-input-group>
                                    <b-form-input
                                        type="number"
                                        :value="subTotal"
                                        name="subTotal"
                                        disabled
                                        class="form-control form-control-sm"
                                    ></b-form-input>
                                </b-input-group>
                            </b-form-group>
                        </b-row>
                        <b-row>
                            <b-form-group>
                                <b-input-group>
                                    <b-form-input
                                        type="number"
                                        v-model="invoice.freightCosts"
                                        name="freightCosts"
                                        :class="{
                                            'is-invalid': v.invoice.freightCosts.$invalid
                                        }"
                                        class="form-control form-control-sm"
                                    ></b-form-input>
                                </b-input-group>
                            </b-form-group>
                        </b-row>
                        <b-row>
                            <b-form-group>
                                <b-input-group>
                                    <b-form-input
                                        type="number"
                                        :class="{
                                            'is-invalid': v.invoice.insuranceCosts.$invalid
                                        }"
                                        v-model="invoice.insuranceCosts"
                                        name="insuranceCosts"
                                        class="form-control form-control-sm"
                                    ></b-form-input>
                                </b-input-group>
                            </b-form-group>
                        </b-row>
                        <b-row>
                            <b-form-group>
                                <b-input-group>
                                    <b-form-input
                                        type="number"
                                        :class="{
                                            'is-invalid': v.invoice.otherCosts.$invalid
                                        }"
                                        v-model="invoice.otherCosts"
                                        name="otherCosts"
                                        class="form-control form-control-sm"
                                    ></b-form-input>
                                </b-input-group>
                            </b-form-group>
                        </b-row>
                        <b-row>
                            <b-form-group>
                                <b-input-group>
                                    <b-form-input
                                        type="number"
                                        v-model="total"
                                        disabled
                                        class="form-control form-control-sm"
                                        name="total"
                                    ></b-form-input>
                                </b-input-group>
                            </b-form-group>
                        </b-row>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
    </b-card>
</template>

<script>
import { mapGetters } from 'vuex';
import vSelect from '../vSelect';
import validationField from '../Validation/ValidationField';
export default {
    components: {
        vSelect,
        validationField
    },
    props: {
        v: { required: true },

        invoice: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            subtotal: 0,
            total: 0,
            unitTypes: [
                { text: 'Kg', value: 'Kg' },
                { text: 'Lbm', value: 'Lbm' },
                { text: 'Cm', value: 'Cm' },
                { text: 'Inch', value: 'Inch' },
                { text: 'Cbm', value: 'Cbm' },
                { text: 'Pieces', value: 'Pieces' }
            ]
        };
    },
    watch: {
        invoice: {
            deep: true,
            immediate: true,
            handler(val, oldVal) {
                this.calculateTotals();
            }
        }
    },
    computed: {
        ...mapGetters('core', ['getCountries'])
    },
    methods: {
        calculateLineTotal(invoiceLine) {
            return (invoiceLine.quantity * invoiceLine.unitPrice).toFixed(2);
        },
        addInvoiceLine() {
            this.invoice.invoiceLines.push({
                requireHsCode: this.invoice.invoiceLines[0].requireHsCode,
                harmonizedCode: '',
                commodity: '',
                countryOfOrigin: '',
                quantity: 0,
                unitType: '',
                unitPrice: 0,
                manufacturer: ''
            });
        },
        calculateTotals() {
            this.subTotal = this.invoice.invoiceLines
                .reduce((a, b) => a + b.unitPrice * b.quantity || 0, 0)
                .toFixed(2);
            this.total = (
                Number(this.invoice.freightCosts) +
                Number(this.invoice.insuranceCosts) +
                Number(this.invoice.otherCosts) +
                Number(this.invoice.customCosts) +
                Number(this.subTotal)
            ).toFixed(2);
        },
        removeInvoiceLine(invoiceLine) {
          const index = this.invoice.invoiceLines.indexOf(invoiceLine);
          if (index > -1) {
            this.invoice.invoiceLines.splice(index, 1);
          }
        }
    }
};
</script>

<style scoped>
.total-text {
    height: calc(1.5rem + 5px);
    font-size: 12px;
    padding: 0.25rem 0rem;
    line-height: 1.5;
    display: block;
    float: right;
}
</style>
